/**
 * The shape `POST /api/auth/login` (and the magic-link redemption) answers with when a second
 * factor is outstanding (`docs/WebAuthn.md` §4.1).
 *
 * Kept apart from the component so both can be imported without dragging a React component
 * into modules that only need the guard.
 */
export type MfaMethod = 'passkey' | 'security_key' | 'recovery_code';

export interface MfaChallenge {
  /**
   * The **strongest** factor this origin can run for this user. A passkey is UV-required —
   * possession plus a PIN or biometric — so it is preferred over a security key when both are
   * enrolled here.
   */
  mfaRequired: MfaMethod;
  challengeId: string;
  /** Absent for `recovery_code`, where no ceremony was started. */
  challenge?: { publicKey: Record<string, unknown> };
  /** Other factors this user holds here, for an explicit "use something else" choice. */
  alternatives?: MfaMethod[];
}

/**
 * Whether a 200 body is a challenge rather than a session.
 *
 * ⚠ **Every caller of `/api/auth/login` needs this.** The gated response is a 200 carrying no
 * cookie and no token; a caller that assumes 200 means "signed in" fails silently, and only for
 * the users who opted into protection.
 */
export function isMfaChallenge(data: unknown): data is MfaChallenge {
  return typeof (data as MfaChallenge | undefined)?.mfaRequired === 'string';
}
